import React from 'react';
import styled from 'styled-components';
import useTranslations from '../hooks/useTranslations';
import LanguageSelector from '../components/LanguageSelector';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem;
`;

const Header = styled.div`
  max-width: 800px;
  margin: 0 auto 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  color: #333;
  font-size: 2.5rem;
  margin: 0;
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  line-height: 1.6;

  h2 {
    color: #FF6B6B;
    margin-top: 2rem;
    margin-bottom: 1rem;
  }

  h3 {
    color: #333;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
  }

  p {
    margin-bottom: 1rem;
    color: #555;
  }

  ul {
    margin-bottom: 1rem;
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.5rem;
    color: #555;
  }
`;

const BackLink = styled.a`
  color: #FF6B6B;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const TermsOfServicePage: React.FC = () => {
  const { t } = useTranslations();

  return (
    <PageContainer>
      <Header>
        <Title>{t('legal.terms.title', 'Termini di Servizio')}</Title>
        <LanguageSelector />
      </Header>
      
      <Content>
        <BackLink href="/">{t('legal.backToHome', '← Torna alla Home')}</BackLink>
        
        <h2>{t('legal.terms.lastUpdated', 'Ultimo aggiornamento: 25 luglio 2025')}</h2>
        
        <p>{t('legal.terms.intro', 'Questi Termini di Servizio regolano l\'utilizzo del servizio Sistema Gelato fornito da krea cloud, Inc.')}</p>

        <h2>{t('legal.terms.section1.title', '1. Accettazione dei Termini')}</h2>
        <p>{t('legal.terms.section1.content', 'Utilizzando il servizio Sistema Gelato, accetti di essere vincolato da questi termini. Se non accetti questi termini, non utilizzare il servizio.')}</p>

        <h2>{t('legal.terms.section2.title', '2. Descrizione del Servizio')}</h2>
        <p>{t('legal.terms.section2.content', 'Sistema Gelato è una piattaforma software as a service (SaaS) per la gestione di gelaterie, che include:')}</p>
        <ul>
          <li>{t('legal.terms.section2.item1', 'Gestione della produzione giornaliera')}</li>
          <li>{t('legal.terms.section2.item2', 'Analytics e reporting avanzati')}</li>
          <li>{t('legal.terms.section2.item3', 'Gestione ingredienti e costi')}</li>
          <li>{t('legal.terms.section2.item4', 'Sistema multi-utente e multi-punto vendita')}</li>
        </ul>

        <h2>{t('legal.terms.section3.title', '3. Registrazione e Account')}</h2>
        <p>{t('legal.terms.section3.content', 'Per utilizzare il servizio devi:')}</p>
        <ul>
          <li>{t('legal.terms.section3.item1', 'Fornire informazioni accurate e complete')}</li>
          <li>{t('legal.terms.section3.item2', 'Mantenere riservate le credenziali di accesso')}</li>
          <li>{t('legal.terms.section3.item3', 'Essere responsabile di tutte le attività del tuo account')}</li>
          <li>{t('legal.terms.section3.item4', 'Notificarci immediatamente di qualsiasi uso non autorizzato')}</li>
        </ul>

        <h2>{t('legal.terms.section4.title', '4. Tariffe e Pagamenti')}</h2>
        <p>{t('legal.terms.section4.content', 'L\'utilizzo del servizio è soggetto alle tariffe pubblicate sul nostro sito web. I pagamenti sono processati tramite Stripe e sono soggetti ai loro termini di servizio.')}</p>

        <h2>{t('legal.terms.section5.title', '5. Uso Accettabile')}</h2>
        <p>{t('legal.terms.section5.content', 'Ti impegni a non:')}</p>
        <ul>
          <li>{t('legal.terms.section5.item1', 'Utilizzare il servizio per scopi illegali')}</li>
          <li>{t('legal.terms.section5.item2', 'Interferire con il funzionamento del servizio')}</li>
          <li>{t('legal.terms.section5.item3', 'Accedere a dati non autorizzati')}</li>
          <li>{t('legal.terms.section5.item4', 'Trasmettere virus o codice dannoso')}</li>
        </ul>

        <h2>{t('legal.terms.section6.title', '6. Proprietà Intellettuale')}</h2>
        <p>{t('legal.terms.section6.content', 'Il servizio Sistema Gelato e tutti i contenuti correlati sono di proprietà di krea cloud, Inc. e protetti dalle leggi sul copyright e altre leggi sulla proprietà intellettuale.')}</p>

        <h2>{t('legal.terms.section7.title', '7. Limitazione di Responsabilità')}</h2>
        <p>{t('legal.terms.section7.content', 'Il servizio è fornito "così com\'è" senza garanzie di alcun tipo. krea cloud, Inc. non sarà responsabile per danni indiretti, incidentali o consequenziali.')}</p>

        <h2>{t('legal.terms.section8.title', '8. Risoluzione')}</h2>
        <p>{t('legal.terms.section8.content', 'Possiamo sospendere o terminare il tuo account in caso di violazione di questi termini. Puoi terminare il tuo account in qualsiasi momento contattandoci.')}</p>

        <h2>{t('legal.terms.section9.title', '9. Legge Applicabile')}</h2>
        <p>{t('legal.terms.section9.content', 'Questi termini sono regolati dalle leggi dello Stato del Delaware, Stati Uniti.')}</p>

        <h2>{t('legal.terms.section10.title', '10. Contatti')}</h2>
        <p>{t('legal.terms.section10.content', 'Per domande riguardo questi Termini di Servizio, contattaci a:')}</p>
        <p>
          <strong>krea cloud, Inc.</strong><br />
          Delaware C Corporation<br />
          Tax ID (EIN): 32-0698209<br />
          651 N Broad St, Suite 201<br />
          Middletown, Delaware 19709<br />
          Website: https://sistemagelato.omai.cloud<br />
          Email: info@omai.cloud<br />
          Telefono (Italia): +39 347 128 1124
        </p>
      </Content>
    </PageContainer>
  );
};

export default TermsOfServicePage;
